import { FC, ReactNode } from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import RetryIcon from "../icons/RetryIcon";
import CircularLoading from "../progress/CircularLoading";
import KeyFrameMessage from "./NoKeyFrameMessage";
import { SxProps, Theme } from "@mui/material/styles";

type MediaStatusVariant =
  | "connecting"
  | "notFound"
  | "keyframeLoading"
  | "keyframeOffline"
  | "keyframeError"
  | "custom";

interface MediaStatusProps {
  variant: MediaStatusVariant;
  retryCount?: number;
  maxRetries?: number;
  minHeight?: string;
  onRetry?: () => void;
  message?: string;
  messageHeight?: string;
  loadingFallback?: ReactNode;
  icon?: ReactNode;
  children?: ReactNode;
  mediaStatusSx?: SxProps<Theme>;
}

const MediaStatus: FC<MediaStatusProps> = ({
  variant,
  retryCount = 0,
  maxRetries,
  minHeight,
  onRetry,
  message,
  messageHeight,
  loadingFallback,
  icon,
  children,
  mediaStatusSx,
}): JSX.Element => {
  const attemptText =
    maxRetries !== undefined
      ? `Trying to connect... attempt ${retryCount + 1} of ${maxRetries}`
      : `Trying to connect... attempt ${retryCount + 1}`;

  let content: ReactNode = children;

  if (!content) {
    switch (variant) {
      case "connecting": {
        content = (
          <>
            <Typography
              component="div"
              variant="inputLabel"
              sx={{ fontSize: "1rem" }}
            >
              {message ?? attemptText}
            </Typography>
            <CircularLoading />
          </>
        );
        break;
      }
      case "notFound": {
        const defaultMessage = (
          <Typography
            component="div"
            variant="inputLabel"
            sx={{ fontSize: "1rem" }}
          >
            The video is not found. <br />
            Please press "retry" button to try again or reach out to
            <br />
            <Typography
              component="span"
              variant="inputLabel"
              sx={{ fontSize: "1rem", "& a": { color: "primary.dark" } }}
            >
              <a href="mailto:support@cleanconnect.ai">
                support@cleanconnect.ai
              </a>
            </Typography>
          </Typography>
        );

        const displayMessage = message ? (
          <Typography
            component="div"
            variant="inputLabel"
            sx={{ fontSize: "1rem" }}
          >
            {message}
          </Typography>
        ) : (
          defaultMessage
        );

        content = (
          <>
            {displayMessage}
            {onRetry && (
              <Button
                sx={{
                  color: "text.secondary",
                  borderColor: "text.secondary",
                  borderWidth: "2px",
                  borderRadius: "40px",
                  "&:hover": {
                    color: "text.secondary",
                    borderColor: "text.secondary",
                  },
                }}
                variant="outlined"
                size="small"
                startIcon={<RetryIcon />}
                onClick={onRetry}
              >
                Retry
              </Button>
            )}
          </>
        );
        break;
      }
      case "keyframeOffline": {
        content = (
          <KeyFrameMessage
            message={message ?? "The device is offline. It can take some time"}
            height={messageHeight}
          />
        );
        break;
      }
      case "keyframeLoading": {
        content = loadingFallback ?? (
          <KeyFrameMessage
            message={message ?? "Please wait for the image to retrieve"}
            height={messageHeight}
          />
        );
        break;
      }
      case "keyframeError": {
        content = (
          <KeyFrameMessage
            message={message ?? "The image is not found or failed to load"}
            height={messageHeight}
          />
        );
        break;
      }
      case "custom":
      default:
        break;
    }
  }

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        minHeight,
        backgroundColor: theme => theme.palette.otherBackground.main,

        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        ...mediaStatusSx,
      }}
    >
      <Stack spacing={2} alignItems="center" justifyContent="center">
        {icon}
        {content}
      </Stack>
    </Box>
  );
};

export default MediaStatus;
